<template>
  <div
    v-if="patient"
    class="section-patient"
  >
    <b-row class="d-flex align-items-center mb-2 mt-1 px-2">
      <b-col>
        <span class="ap-title ap-title__main">{{ $t('globalLocator.labels.patientInformation') }}</span>
      </b-col>
    </b-row>
    <b-row class="px-2">
      <b-col class="form-box mt-2">
        <div class="structure">
          <div class="structure__heading">
            <i
              class="far fa-user mr-2"
              aria-hidden="true"
            />{{ $t('global.personalData') }}
          </div>
          <b-row>
            <b-col
              v-if="globalParameters.mainPatientIdentifierType"
              md="3"
              class="structure__data custom-width"
            >
              <small>{{ globalParameters.mainPatientIdentifierType.name }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.mainPatientIdentifier ? patient.mainPatientIdentifier.paiIdentifier : '-' }}
              </h4>
            </b-col>
            <b-col
              v-if="globalParameters.secondaryPatientIdentifierType"
              md="3"
              class="structure__data custom-width"
            >
              <small>{{ globalParameters.secondaryPatientIdentifierType.name }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.secondaryPatientIdentifier ? patient.secondaryPatientIdentifier.paiIdentifier : '-' }}
              </h4>
            </b-col>
          </b-row>
          <div class="horizontal-divider my-3" />
          <b-row>
            <b-col
              md="3"
              class="structure__data"
            >
              <small>{{ $tc('global.name',1) }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.name }}
              </h4>
            </b-col>
            <b-col
              v-if="globalParameters.middleNameSupported"
              md="3"
              class="structure__data"
            >
              <small>{{ $t('global.middleName') }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.middleName }}
              </h4>
            </b-col>
            <b-col
              md="3"
              class="structure__data"
            >
              <small>{{ $t('global.surname1') }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.surname1 }}
              </h4>
            </b-col>
            <b-col
              v-if="globalParameters.secondSurnameSupported"
              md="3"
              class="structure__data"
            >
              <small>{{ $t('global.surname2') }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.surname2 }}
              </h4>
            </b-col>
            <b-col
              md="3"
              class="structure__data"
            >
              <small>{{ $t('global.birthday') }}</small>
              <h4
                v-if="patient.birthdate"
                class="collapsable-card-title inside"
              >
                {{ patient.birthdate | date }} ({{ age }})
              </h4>
            </b-col>
            <b-col
              md="3"
              class="structure__data"
            >
              <small>{{ $tc('global.gender') }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.gender && patient.gender.name }}
              </h4>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
    <b-row
      v-if="patient.address"
      class="px-2"
    >
      <b-col class="form-box">
        <div class="structure">
          <div class="structure__heading">
            <i
              class="far fa-envelope mr-2"
              aria-hidden="true"
            />{{ $t('global.contactData') }}
          </div>
          <b-row>
            <b-col
              md="3"
              class="structure__data"
            >
              <small>{{ $tc('global.address', 1) }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.address.address }}
              </h4>
            </b-col>
            <b-col
              md="3"
              class="structure__data"
            >
              <small>{{ $t('global.pc') }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.address.postalCode }}
              </h4>
            </b-col>
            <b-col
              md="3"
              class="structure__data"
            >
              <small>{{ $tc('global.municipality',1) }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.address.municipality && patient.address.municipality.name }}
              </h4>
            </b-col>
            <b-col
              md="3"
              class="structure__data"
            >
              <small>{{ $tc('global.province',1) }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.address.province && patient.address.province.name }}
              </h4>
            </b-col>
            <b-col
              md="3"
              class="structure__data"
            >
              <small>{{ $tc('global.country',1) }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.address.country && patient.address.country.name }}
              </h4>
            </b-col>
            <b-col
              md="3"
              class="structure__data"
            >
              <small>{{ $tc('global.phone',1) }} 1</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.contactInformation.mainPhone }}
              </h4>
            </b-col>
            <b-col
              md="3"
              class="structure__data"
            >
              <small>{{ $tc('global.phone',1) }} 2</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.contactInformation.otherPhone1 }}
              </h4>
            </b-col>
            <b-col
              md="3"
              class="structure__data"
            >
              <small>{{ $tc('global.phone',1) }} 3</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.contactInformation.otherPhone2 }}
              </h4>
            </b-col>
            <b-col
              md="6"
              class="structure__data"
            >
              <small>{{ $tc('global.email',1) }}</small>
              <h4 class="collapsable-card-title inside">
                {{ patient.contactInformation.email }}
              </h4>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
    <b-row class="px-2">
      <b-col class="form-box">
        <div class="structure">
          <div class="structure__heading">
            <i
              class="far fa-comment-lines mr-2"
              aria-hidden="true"
            />{{ $t('global.observations') }}
          </div>
          <b-row class="d-flex pl-3 pr-3">
            <div class="col pl-0 pr-0 structure__data">
              <h4
                v-if="patient.notes != null"
                class="collapsable-card-title inside"
              >
                {{ patient.notes }}
              </h4>
              <h4
                v-else
                class="collapsable-card-title inside"
              >
                {{ $t('global.noObservations') }}
              </h4>
            </div>
          </b-row>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import moment from 'moment'
import patientService from '@/api/resources/common/commonPatientServices'
import { mountPatientAgeString } from '@/utils/fieldTransformations'

export default {
    name: 'PatientSection',
    props: {
        patientId: { type: Number, required: true }
    },
    data() {
        return {
            patient: null
        }
    },
    computed: {
        age() {
            if (this.patient.birthdate) {
                const daysOfPatient = moment().diff(this.patient.birthdate, 'days')
                return mountPatientAgeString(daysOfPatient)
            }
            return undefined
        }
    },
    watch: {
        async patientId() {
            await this.loadPatient()
        }
    },
    methods: {
        async loadPatient() {
            this.patient =
                await this.$_toCallSecure(
                    patientService.getPatientInformation(this.$store.getters.labId, this.patientId))
        }
    }
}
</script>
